import { MutationTree } from "vuex";
import { State } from "@/store";

import {
  LabelShapes,
  EditorParams,
  AccordianState,
  UserArtworkFetch,
  ColorPickrState,
  LabelSizes,
  getSheetLabelProps,
  getCurrentLabelProps,
  validateSheetLabelProps,
  validateCurrentLabelProps,
  ClientDimensions,
  CurrentLabel,
  UrlProperties,
  SettingsState,
  LabelSVG,
  SheetLabel,
  Dimensions,
} from "@/types";

import { getEditorUrlParam } from "@/composables/useParseURL";

export enum MutationTypes {
  SET_MODAL = "SET_MODAL",
  SET_SHOW_ONBOARDING = "SET_SHOW_ONBOARDING",

  START_FETCHING_USER_ARTWORK = "START_FETCHING_USER_ARTWORK",
  FETCH_USER_ARTWORK_SUCCESS = "FETCH_USER_ARTWORK_SUCCESS",
  FETCH_USER_ARTWORK_FAILURE = "FETCH_USER_ARTWORK_FAILURE",

  START_TEMPLATE_AND_LABEL = "START_TEMPLATE_AND_LABEL",
  TEMPLATE_AND_LABEL_SUCCESS = "TEMPLATE_AND_LABEL_SUCCESS",
  TEMPLATE_AND_LABEL_FAILURE = "TEMPLATE_AND_LABEL_FAILURE",

  START_FETCHING_SIZES = "START_FETCHING_SIZES",
  FETCH_SIZES_SUCCESS = "FETCH_SIZES_SUCCESS",
  FETCH_SIZES_FAILURE = "FETCH_SIZES_FAILURE",

  START_FETCHING_CUSTOM_SIZES = "START_FETCHING_CUSTOM_SIZES",
  FETCH_CUSTOM_SIZES_SUCCESS = "FETCH_CUSTOM_SIZES_SUCCESS",
  FETCH_CUSTOM_SIZES_FAILURE = "FETCH_CUSTOM_SIZES_FAILURE",

  START_FETCHING_SHAPES = "START_FETCHING_SHAPES",
  FETCH_SHAPES_SUCCESS = "FETCH_SHAPES_SUCCESS",
  FETCH_SHAPES_FAILURE = "FETCH_SHAPES_FAILURE",

  START_FETCHING_EDITOR_PARAMS = "START_FETCHING_EDITOR_PARAMS",
  FETCH_EDITOR_PARAMS_SUCCESS = "FETCH_EDITOR_PARAMS_SUCCESS",
  FETCH_EDITOR_PARAMS_FAILURE = "FETCH_EDITOR_PARAMS_FAILURE",

  START_FETCHING_MATERIALS = "START_FETCHING_MATERIALS",
  FETCH_MATERIALS_SUCCESS = "FETCH_MATERIALS_SUCCESS",
  FETCH_MATERIALS_FAILURE = "FETCH_MATERIALS_FAILURE",

  SET_CUSTOM_LABEL = "SET_CUSTOM_LABEL",

  CHANGE_LABEL_SIZE = "CHANGE_LABEL_SIZE",
  CHANGE_CUSTOM_LABEL_SIZE = "CHANGE_CUSTOM_LABEL_SIZE",
  CHANGE_LABEL_MATERIAL = "CHANGE_LABEL_MATERIAL",

  SET_MOUNTING_LABEL = "SET_MOUNTING_LABEL",
  SET_CLIENT_DIMENSIONS = "SET_CLIENT_DIMENSIONS",
  SET_LABEL_BG_COLOR = "SET_LABEL_BG_COLOR",
  SET_USER_ARTWORK_FETCH = "SET_USER_ARTWORK_FETCH",
  SET_ACCORDIAN_STATE = "SET_ACCORDIAN_STATE",
  SET_COLOR_PICKR_STATE = "SET_COLOR_PICKR_STATE",
  SET_CANVAS_ACTIVE_OBJECT = "SET_CANVAS_ACTIVE_OBJECT",
  SET_SETTINGS_STATE = "SET_SETTINGS_STATE",
  SET_CANVAS_ORIENTATION = "SET_CANVAS_ORIENTATION",
  SET_LABEL_SVG = "SET_LABEL_SVG",
}

export type Mutations<S = State> = {
  [MutationTypes.SET_MODAL](state: S, payload: string): void;
  [MutationTypes.SET_SHOW_ONBOARDING](state: S, payload: boolean): void;
  [MutationTypes.START_FETCHING_USER_ARTWORK](state: S): void;
  [MutationTypes.FETCH_USER_ARTWORK_SUCCESS](
    state: S,
    { urlProperties, data }: { urlProperties: UrlProperties; data: any }
  ): void;
  [MutationTypes.FETCH_USER_ARTWORK_FAILURE](state: S, error: any): void;

  [MutationTypes.START_FETCHING_EDITOR_PARAMS](state: S): void;
  [MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS](
    state: S,
    { labelType, params, label }: { labelType: string; params: EditorParams; label: any }
  ): void;
  [MutationTypes.FETCH_EDITOR_PARAMS_FAILURE](state: S, error: any): void;

  [MutationTypes.START_TEMPLATE_AND_LABEL](state: S): void;
  [MutationTypes.TEMPLATE_AND_LABEL_SUCCESS](
    state: S,
    {
      label,
      template,
      templateJson,
      templateParams,
    }: { label: any; template: any; templateJson: any; templateParams: any }
  ): void;
  [MutationTypes.TEMPLATE_AND_LABEL_FAILURE](state: S, error: any): void;

  [MutationTypes.START_FETCHING_SIZES](state: S): void;
  [MutationTypes.FETCH_SIZES_SUCCESS](
    state: S,
    { data, shapeId, setDefault }: { data: any[]; shapeId?: number; setDefault?: boolean }
  ): void;
  [MutationTypes.FETCH_SIZES_FAILURE](state: S, error: any): void;

  [MutationTypes.START_FETCHING_CUSTOM_SIZES](state: S): void;
  [MutationTypes.FETCH_CUSTOM_SIZES_SUCCESS](
    state: State,
    { data, shapeId, setDefault }: { data: any[]; shapeId: number; setDefault?: boolean }
  ): void;
  [MutationTypes.FETCH_CUSTOM_SIZES_FAILURE](state: S, error: any): void;

  [MutationTypes.START_FETCHING_SHAPES](state: S): void;
  [MutationTypes.FETCH_SHAPES_SUCCESS](state: S, data: LabelShapes[]): void;
  [MutationTypes.FETCH_SHAPES_FAILURE](state: S, error: any): void;

  [MutationTypes.START_FETCHING_MATERIALS](state: S): void;
  [MutationTypes.FETCH_MATERIALS_SUCCESS](
    state: State,
    { data, materialId }: { data: any; materialId: string }
  ): void;
  [MutationTypes.FETCH_MATERIALS_FAILURE](state: S, error: any): void;

  [MutationTypes.SET_CUSTOM_LABEL](
    state: S,
    { labelType, label }: { labelType: string; label: any }
  ): void;

  [MutationTypes.CHANGE_LABEL_SIZE](state: S, label: LabelSizes): void;
  [MutationTypes.CHANGE_CUSTOM_LABEL_SIZE](state: State, payload: CurrentLabel): void;
  [MutationTypes.CHANGE_LABEL_MATERIAL](state: State, payload: any): void;

  [MutationTypes.SET_MOUNTING_LABEL](state: State, payload: boolean): void;
  [MutationTypes.SET_CLIENT_DIMENSIONS](state: State, payload: ClientDimensions): void;
  [MutationTypes.SET_LABEL_BG_COLOR](state: State, payload: string): void;
  [MutationTypes.SET_USER_ARTWORK_FETCH](state: State, payload: UserArtworkFetch): void;
  [MutationTypes.SET_ACCORDIAN_STATE](state: S, payload: AccordianState): void;
  [MutationTypes.SET_COLOR_PICKR_STATE](state: S, payload: ColorPickrState): void;
  [MutationTypes.SET_SETTINGS_STATE](state: S, payload: string | SettingsState): void;
  [MutationTypes.SET_CANVAS_ACTIVE_OBJECT](state: S, payload: fabric.Object): void;
  [MutationTypes.SET_CANVAS_ORIENTATION](state: S, payload: string): void;
  [MutationTypes.SET_LABEL_SVG](state: State, payload: LabelSVG): void;
};

export const mutations: MutationTree<State> & Mutations = {
  [MutationTypes.SET_MODAL](state: State, payload: string): void {
    state.modal = payload;
  },
  [MutationTypes.SET_SHOW_ONBOARDING](state: State, payload: boolean): void {
    state.showOnboarding = payload;
  },
  //USER ARTWORK FETCH PARAMS
  [MutationTypes.START_FETCHING_USER_ARTWORK](state: State): void {
    state.userArtworkFetch = {
      ...state.userArtworkFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_USER_ARTWORK_SUCCESS](
    state: State,
    { urlProperties, data }: { urlProperties: UrlProperties; data: any }
  ): void {
    const params = data.params;
    const artwork = data.artwork;
    let fabric_objects = data.fabric_objects;
    let label = data.label;

    if (urlProperties.isCustom) {
      state.currentLabelType = `custom-${urlProperties.labelType}`;
    } else {
      state.currentLabelType = urlProperties.labelType;
    }

    if (JSON.parse(fabric_objects)) {
      fabric_objects = JSON.parse(fabric_objects);

      state.initialize = "userArtwork";
    } else {
      console.log("No json");
    }

    if (
      artwork &&
      artwork.orientation &&
      artwork.orientation === "portrait" &&
      Number(artwork.width) === Number(artwork.height)
    ) {
      state.canvasOrientation = "rotated";
    } else if (
      artwork &&
      artwork.orientation &&
      artwork.orientation === "portrait" &&
      Number(artwork.width) > Number(artwork.height)
    ) {
      state.canvasOrientation = "rotated";
    } else if (
      artwork &&
      artwork.orientation &&
      artwork.orientation === "landscape" &&
      Number(artwork.height) > Number(artwork.width)
    ) {
      state.canvasOrientation = "rotated";
    }

    if (label) {
      switch (urlProperties.labelType) {
        case "blank":
        case "printed": {
          state.currentLabelType = "printed";

          label = { ...params, ...label };

          if (!label.corners) {
            label.corners = "None";
          }

          if (!label.corner_radius) {
            if (label.corners.toLowerCase() === "rounded") {
              label.corner_radius = 0.125;
            } else {
              label.corner_radius = 0;
            }
          }

          if (label.shape_title && label.shape_title.toLowerCase().includes("oval")) {
            label.shape_id = 5;
          }

          if (artwork && artwork.corners && artwork.corners.toLowerCase() === "rounded") {
            label.corners = "Rounded";

            if (artwork.corner_radius) {
              label.corner_radius = artwork.corner_radius;
            }
          }

          const labelProps = getSheetLabelProps(label);

          if (validateSheetLabelProps(labelProps)) {
            state.dimensions = getSheetLabelDimensions(labelProps as SheetLabel, state.dimensions);
            state.currentLabel = validateSheetLabelProps(labelProps);
          } else {
            throw new Error(
              `Error when validating label, MutationTypes.FETCH_USER_ARTWORK_SUCCESS`
            );
          }

          state.editorParamsFetch = {
            ...state.editorParamsFetch,
            isFetching: false,
            payload: label,
          };
          break;
        }
        case "flyer":
        case "magnet":
        case "rollprinted": {
          switch (urlProperties.labelType) {
            case "flyer": {
              state.currentLabelType = "flyer";
              break;
            }
            case "magnet": {
              state.currentLabelType = "magnet";
              break;
            }
            case "rollprinted": {
              state.currentLabelType = "rollprinted";
              break;
            }
          }

          label = { ...params, ...label };

          if (!label.corners) {
            label.corners = "None";
          }

          if (!label.corner_radius) {
            if (label.corners.toLowerCase() === "rounded") {
              label.corner_radius = 0.125;
            } else {
              label.corner_radius = 0;
            }
          }

          if (artwork && artwork.corners && artwork.corners.toLowerCase() === "rounded") {
            label.corners = "Rounded";

            if (artwork.corner_radius) {
              label.corner_radius = artwork.corner_radius;
            }
          }

          if (!label.label_name) {
            label.label_name = "User Artwork";
          }

          const labelProps = getCurrentLabelProps(label);

          if (validateCurrentLabelProps(labelProps)) {
            if (state.currentLabelType === "rollprinted") {
              state.dimensions = {
                units: {
                  dpi: 96,
                  offSet: 0.0625,
                  offSetPx: 0.0625 * 96,
                },
                radius: Number(labelProps.width) / 2,
                width: Number(labelProps.width),
                height: Number(labelProps.height),
              };
            } else {
              state.dimensions = {
                ...state.dimensions,
                radius: Number(labelProps.width) / 2,
                width: Number(labelProps.width),
                height: Number(labelProps.height),
              };
            }

            state.currentLabel = validateCurrentLabelProps(labelProps);
          } else {
            throw new Error(
              `Error when validating label, MutationTypes.FETCH_USER_ARTWORK_SUCCESS`
            );
          }

          state.editorParamsFetch = {
            ...state.editorParamsFetch,
            isFetching: false,
            payload: label,
          };
          break;
        }
      }
    }

    state.editorParamsFetch = {
      ...state.editorParamsFetch,
      isFetching: false,
      payload: params,
    };

    state.userArtworkFetch = {
      ...state.userArtworkFetch,
      isFetching: false,
      payload: {
        params,
        label,
        artwork,
        fabric_objects,
      },
    };
  },
  [MutationTypes.FETCH_USER_ARTWORK_FAILURE](state: State, error: any): void {
    state.userArtworkFetch = {
      ...state.userArtworkFetch,
      isFetching: false,
      error: error,
    };
  },

  //EDITOR PARAMS
  [MutationTypes.START_FETCHING_EDITOR_PARAMS](state: State): void {
    state.editorParamsFetch = {
      ...state.editorParamsFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS](
    state: State,
    { labelType, params, label }: { labelType: string; params: EditorParams; label: any }
  ): void {
    if (label) {
      switch (labelType) {
        case "blank":
        case "printed": {
          state.currentLabelType = labelType;

          label = { ...params, ...label };

          if (getEditorUrlParam("material_id").length) {
            label["material_id"] = getEditorUrlParam("material_id");
          } else {
            label["material_id"] = 1;
          }

          if (label.shape_title && label.shape_title.toLowerCase().includes("oval")) {
            label.shape_id = 5;
          }

          const labelProps = getSheetLabelProps(label);

          if (validateSheetLabelProps(labelProps)) {
            state.dimensions = getSheetLabelDimensions(labelProps as SheetLabel, state.dimensions);
            state.currentLabel = validateSheetLabelProps(labelProps);
          } else {
            throw new Error(
              `Error when validating label, MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS`
            );
          }

          state.editorParamsFetch = {
            ...state.editorParamsFetch,
            isFetching: false,
            payload: label,
          };
          break;
        }
        case "flyer":
        case "magnet":
        case "rollprinted": {
          switch (labelType) {
            case "flyer": {
              state.currentLabelType = "flyer";
              break;
            }
            case "magnet": {
              state.currentLabelType = "magnet";
              break;
            }
            case "rollprinted": {
              state.currentLabelType = "rollprinted";
              break;
            }
          }

          label = { ...params, ...label };

          if (!label.corners) {
            label.corners = "None";
          }

          if (!label.corner_radius) {
            if (label.corners.toLowerCase() === "rounded") {
              label.corner_radius = 0.125;
            } else {
              label.corner_radius = 0;
            }
          }

          if (!label.label_name) {
            label.label_name = "Missing name";
          }

          if (getEditorUrlParam("material_id").length) {
            label["material_id"] = getEditorUrlParam("material_id");
          } else {
            label["material_id"] = 1;
          }

          const labelProps = getCurrentLabelProps(label);

          if (validateCurrentLabelProps(labelProps)) {
            if (state.currentLabelType === "rollprinted") {
              state.dimensions = {
                units: {
                  dpi: 96,
                  offSet: 0.0625,
                  offSetPx: 0.0625 * 96,
                },
                radius: Number(labelProps.width) / 2,
                width: Number(labelProps.width),
                height: Number(labelProps.height),
              };
            } else {
              state.dimensions = {
                ...state.dimensions,
                radius: Number(labelProps.width) / 2,
                width: Number(labelProps.width),
                height: Number(labelProps.height),
              };
            }

            state.currentLabel = validateCurrentLabelProps(labelProps);
          } else {
            throw new Error(
              `Error when validating label, MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS`
            );
          }

          state.editorParamsFetch = {
            ...state.editorParamsFetch,
            isFetching: false,
            payload: label,
          };
          break;
        }
      }
    } else {
      state.editorParamsFetch = {
        ...state.editorParamsFetch,
        isFetching: false,
        payload: params,
      };
    }
  },
  [MutationTypes.FETCH_EDITOR_PARAMS_FAILURE](state: State, error: any): void {
    state.editorParamsFetch = {
      ...state.editorParamsFetch,
      isFetching: false,
      error: error,
    };
  },

  //TEMPLATE AND LABEL
  [MutationTypes.START_TEMPLATE_AND_LABEL](state: State): void {
    state.templateAndLabelFetch = {
      ...state.templateAndLabelFetch,
      isFetching: true,
    };
  },
  // prettier-ignore
  [MutationTypes.TEMPLATE_AND_LABEL_SUCCESS](
		state: State,
		{ label, template, templateJson, templateParams, }: { label: any; template: any; templateJson: any; templateParams: any }
	): void {
		if (JSON.parse(templateJson)) {
			templateJson = JSON.parse(templateJson);
			state.initialize = "template";
		} else {
			console.log("No json");
		}

		if (templateParams && templateParams.product_type) {
			state.currentLabelType = templateParams.product_type;
		}

		if (!label.label_name) {
			label.label_name = "Template";
			label.name = "Template";
		}

		if (!label.corners) {
			label.corners = "None";
		}

		if (!label.corner_radius) {
			if (label.corners.toLowerCase() === "rounded") {
				label.corner_radius = 0.125;
			} else {
				label.corner_radius = 0;
			}
		}
    
		if (label && label.width && label.height && template.orientation) {
			if (Number(label.width) > Number(label.height) && template.orientation === "portrait") {
				state.canvasOrientation = "rotated";
			} else if (Number(label.height) > Number(label.width) && template.orientation === "landscape" ) {
				state.canvasOrientation = "rotated";
			}
		}

		switch (state.currentLabelType) {
			case "printed": {
				state.currentLabelType = "printed";

				label = { ...templateParams, ...label };

				if (getEditorUrlParam("material_id").length) {
					label["material_id"] = getEditorUrlParam("material_id");
				} else {
					label["material_id"] = 1;
				}

				if (label.shape_title && label.shape_title.toLowerCase().includes("oval")) {
					label.shape_id = 5;
				}

				const labelProps = getSheetLabelProps(label);

				if (validateSheetLabelProps(labelProps)) {
					state.dimensions = getSheetLabelDimensions(labelProps as SheetLabel, state.dimensions);
            state.currentLabel = validateSheetLabelProps(labelProps);
				} else {
					throw new Error(`Error when validating label, MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS`);
				}

				state.templateAndLabelFetch = {
					...state.templateAndLabelFetch,
					isFetching: false,
					payload: {
						templateJson,
						template,
						label,
					},
				};

				state.editorParamsFetch = {
					...state.editorParamsFetch,
					payload: templateParams,
				};
				break;
			}
			case "flyer":
			case "magnet":
			case "rollprinted": {
				label = { ...templateParams, ...label };

				if (!label.corners) {
					label.corners = "None";
				}

				if (!label.corner_radius) {
					if (label.corners.toLowerCase() === "rounded") {
						label.corner_radius = 0.125;
					} else {
						label.corner_radius = 0;
					}
				}

				if (!label.label_name) {
					label.label_name = "Missing name";
				}

				if (getEditorUrlParam("material_id").length) {
					label["material_id"] = getEditorUrlParam("material_id");
				} else {
					label["material_id"] = 1;
				}

				const labelProps = getCurrentLabelProps(label);

				if (validateCurrentLabelProps(labelProps)) {
          if(state.currentLabelType === "rollprinted") {
            state.dimensions = {
              units: {
                dpi: 96,
                offSet: 0.0625,
                offSetPx: 0.0625 * 96,
              },
              radius: Number(labelProps.width) / 2,
              width: Number(labelProps.width),
              height: Number(labelProps.height),
            };
          } else {
            state.dimensions = {
              ...state.dimensions,
              radius: Number(labelProps.width) / 2,
              width: Number(labelProps.width),
              height: Number(labelProps.height),
            };
          }

					state.currentLabel = validateCurrentLabelProps(labelProps);

					state.templateAndLabelFetch = {
						...state.templateAndLabelFetch,
						isFetching: false,
						payload: {
							templateJson,
							template,
							label,
						},
					};

					state.editorParamsFetch = {
						...state.editorParamsFetch,
						payload: templateParams,
					};
				} else {
					throw new Error(`Error when validating label, MutationTypes.FETCH_EDITOR_PARAMS_SUCCESS`);
				}
				break;
			}
		}
	},
  [MutationTypes.TEMPLATE_AND_LABEL_FAILURE](state: State, error: any): void {
    state.templateAndLabelFetch = {
      ...state.templateAndLabelFetch,
      isFetching: false,
      error: error,
    };
  },

  //CUSTOM SIZES
  [MutationTypes.START_FETCHING_CUSTOM_SIZES](state: State): void {
    state.customLabelSizesFetch = {
      ...state.customLabelSizesFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_CUSTOM_SIZES_SUCCESS](
    state: State,
    { data, shapeId, setDefault }: { data: any[]; shapeId: number; setDefault?: boolean }
  ): void {
    state.customLabelSizesFetch = {
      ...state.customLabelSizesFetch,
      isFetching: false,
      payload: data,
    };

    if (setDefault) {
      state.currentLabel = {
        ...state.currentLabel,
        shape_id: shapeId,
      };

      if (shapeId !== 10 && shapeId !== 3) {
        state.dimensions = {
          ...state.dimensions,
          radius: Number(state.currentLabel.width) / 2,
          width: Number(state.currentLabel.width),
          height: Number(state.currentLabel.height),
        };
      } else {
        state.dimensions = {
          ...state.dimensions,
          radius: Number(state.currentLabel.width) / 2,
          width: Number(state.currentLabel.width),
          height: Number(state.currentLabel.width),
        };
      }
    }
  },
  [MutationTypes.FETCH_CUSTOM_SIZES_FAILURE](state: State, error: any): void {
    state.customLabelSizesFetch = {
      ...state.customLabelSizesFetch,
      isFetching: false,
      error: error,
    };
  },

  //SHAPES
  [MutationTypes.START_FETCHING_SHAPES](state: State): void {
    state.labelShapesFetch = {
      ...state.labelShapesFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_SHAPES_SUCCESS](state: State, data: LabelShapes[]): void {
    state.labelShapesFetch = {
      ...state.labelShapesFetch,
      isFetching: false,
      payload: data,
    };
  },
  [MutationTypes.FETCH_SHAPES_FAILURE](state: State, error: any): void {
    state.labelShapesFetch = {
      ...state.labelShapesFetch,
      isFetching: false,
      error: error,
    };
  },
  //Materials
  [MutationTypes.START_FETCHING_MATERIALS](state: State): void {
    state.materialsFetch = {
      ...state.materialsFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_MATERIALS_SUCCESS](
    state: State,
    { data, materialId }: { data: any; materialId: string }
  ): void {
    if (!Number.isNaN(Number(materialId))) {
      state.currentLabelMaterialId = Number(materialId);
    }

    state.materialsFetch = {
      ...state.materialsFetch,
      isFetching: false,
      payload: data,
    };
  },
  [MutationTypes.FETCH_MATERIALS_FAILURE](state: State, error: any): void {
    state.materialsFetch = {
      ...state.materialsFetch,
      isFetching: false,
      error: error,
    };
  },

  [MutationTypes.SET_CUSTOM_LABEL](
    state: State,
    { labelType, label }: { labelType: string; label: any }
  ): void {
    state.currentLabelType = labelType;

    const labelProps = getCurrentLabelProps(label);

    if (validateCurrentLabelProps(labelProps)) {
      state.dimensions = {
        ...state.dimensions,
        radius: Number(labelProps.width) / 2,
        width: Number(labelProps.width),
        height: Number(labelProps.height),
      };

      state.currentLabel = validateCurrentLabelProps(labelProps);
    } else {
      throw new Error(`Error when validating label, MutationTypes.SET_CUSTOM_LABEL`);
    }
  },

  [MutationTypes.CHANGE_LABEL_SIZE](state: State, label: LabelSizes): void {
    switch (state.currentLabelType) {
      case "printed": {
        const labelProps = getSheetLabelProps(label);

        if (validateSheetLabelProps(labelProps)) {
          state.dimensions = getSheetLabelDimensions(labelProps as SheetLabel, state.dimensions);
          state.currentLabel = validateSheetLabelProps(labelProps);
          state.canvasOrientation = "default";
        } else {
          throw new Error(`Error when validating label, MutationTypes.CHANGE_LABEL_SIZE`);
        }
        break;
      }
      case "rollprinted": {
        const labelProps = getCurrentLabelProps(label);

        if (validateCurrentLabelProps(labelProps)) {
          state.dimensions = {
            ...state.dimensions,
            radius: Number(labelProps.width) / 2,
            width: Number(labelProps.width),
            height: Number(labelProps.height),
          };

          state.currentLabel = validateCurrentLabelProps(labelProps);
          state.canvasOrientation = "default";
        } else {
          throw new Error(`Error when validating label, MutationTypes.CHANGE_LABEL_SIZE`);
        }
        break;
      }
    }
  },
  [MutationTypes.CHANGE_CUSTOM_LABEL_SIZE](state: State, payload: CurrentLabel): void {
    if (payload.shape_id !== 10 && payload.shape_id !== 3) {
      state.dimensions = {
        ...state.dimensions,
        radius: Number(payload.width) / 2,
        width: Number(payload.width),
        height: Number(payload.height),
      };
    } else {
      state.dimensions = {
        ...state.dimensions,
        radius: Number(payload.width) / 2,
        width: Number(payload.width),
        height: Number(payload.width),
      };
    }

    state.currentLabel = payload;
  },
  [MutationTypes.CHANGE_LABEL_MATERIAL](state: State, payload: any): void {
    const material = payload;
    if (material && material.id) {
      state.currentLabelMaterialId = Number(material.id);
    }
  },

  //APPLICATION STATE
  [MutationTypes.SET_MOUNTING_LABEL](state: State, payload: boolean): void {
    state.mountingLabel = payload;
  },
  [MutationTypes.SET_CLIENT_DIMENSIONS](state: State, payload: ClientDimensions): void {
    state.clientDimensions = payload;
  },
  [MutationTypes.SET_LABEL_BG_COLOR](state: State, payload: string): void {
    state.currentLabelBgColor = payload;
  },
  [MutationTypes.SET_USER_ARTWORK_FETCH](state: State, payload: UserArtworkFetch): void {
    state.userArtworkFetch = payload;
  },
  [MutationTypes.SET_ACCORDIAN_STATE](state: State, payload: AccordianState): void {
    state.accordianState = payload;
  },
  [MutationTypes.SET_COLOR_PICKR_STATE](state: State, payload: ColorPickrState): void {
    state.colorPickrState = payload;
  },
  [MutationTypes.SET_SETTINGS_STATE](state: State, payload: string | SettingsState): void {
    if (typeof payload === "string") {
      switch (payload) {
        case "guidelines": {
          state.settingsState = {
            ...state.settingsState,
            guidelines: !state.settingsState.guidelines,
          };
          break;
        }
        case "rulers": {
          state.settingsState = {
            ...state.settingsState,
            rulers: !state.settingsState.rulers,
          };
          break;
        }
        case "grid": {
          state.settingsState = {
            ...state.settingsState,
            grid: !state.settingsState.grid,
          };
          break;
        }
      }
    } else {
      state.settingsState = payload;
    }
  },
  [MutationTypes.SET_CANVAS_ACTIVE_OBJECT](state: State, payload: fabric.Object): void {
    state.currentActiveObject = payload;
  },
  [MutationTypes.SET_CANVAS_ORIENTATION](state: State, payload: string): void {
    state.canvasOrientation = payload;
  },
  [MutationTypes.SET_LABEL_SVG](state: State, payload: LabelSVG): void {
    state.labelSVG = payload;
  },

  //SIZES
  [MutationTypes.START_FETCHING_SIZES](state: State): void {
    state.labelSizesFetch = {
      ...state.labelSizesFetch,
      isFetching: true,
    };
  },
  [MutationTypes.FETCH_SIZES_SUCCESS](
    state: State,
    { data, shapeId, setDefault }: { data: any[]; shapeId?: number; setDefault?: boolean }
  ): void {
    state.labelSizesFetch = {
      ...state.labelSizesFetch,
      isFetching: false,
      payload: data,
    };

    //For when user changes label shape and the shapes associated labels need to be fetched
    if (shapeId && setDefault) {
      switch (state.currentLabelType) {
        case "printed": {
          switch (shapeId) {
            case 2: {
              const defaultLabel = data.find((label: any) => label.name === "SL127");

              if (!defaultLabel) {
                throw new Error("No default label present, case 2");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 3: {
              const defaultLabel = data.find((label: any) => label.name === "SL1485");

              if (!defaultLabel) {
                throw new Error("No default label present, case 3");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 4: {
              const defaultLabel = data.find((label: any) => label.name === "SL102");

              if (!defaultLabel) {
                throw new Error("No default label present, case 4");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 5: {
              const defaultLabel = data.find((label: any) => label.name === "SL609");

              if (!defaultLabel) {
                throw new Error("No default label present, case 5");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 6: {
              const defaultLabel = data.find((label: any) => label.name === "SL293");

              if (!defaultLabel) {
                throw new Error("No default label present, case 6");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 7: {
              const defaultLabel = data.find((label: any) => label.name === "SL126");

              if (!defaultLabel) {
                throw new Error("No default label present, case 7");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 8: {
              const defaultLabel = data.find((label: any) => label.name === "SL722");

              if (!defaultLabel) {
                throw new Error("No default label present, case 8");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 10: {
              const defaultLabel = data.find((label: any) => label.name === "SL228");

              if (!defaultLabel) {
                throw new Error("No default label present, case 10");
              } else {
                const labelProps = getSheetLabelProps(defaultLabel);

                if (validateSheetLabelProps(labelProps)) {
                  state.dimensions = getSheetLabelDimensions(
                    labelProps as SheetLabel,
                    state.dimensions
                  );
                  state.currentLabel = validateSheetLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            default: {
              throw new Error("No Shape Id!");
            }
          }

          break;
        }
        case "rollprinted": {
          switch (shapeId) {
            case 2: {
              break;
            }
            case 3: {
              const defaultLabel = data.find((label: any) => label.name === "SLR1285");

              if (!defaultLabel) {
                throw new Error("No default label present, case 3");
              } else {
                const labelProps = getCurrentLabelProps(defaultLabel);

                if (validateCurrentLabelProps(labelProps)) {
                  state.dimensions = {
                    ...state.dimensions,
                    radius: Number(labelProps.width) / 2,
                    width: Number(labelProps.width),
                    height: Number(labelProps.height),
                  };

                  state.currentLabel = validateCurrentLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 4: {
              const defaultLabel = data.find((label: any) => label.name === "SLR1277");

              if (!defaultLabel) {
                throw new Error("No default label present, case 4");
              } else {
                const labelProps = getCurrentLabelProps(defaultLabel);

                if (validateCurrentLabelProps(labelProps)) {
                  state.dimensions = {
                    ...state.dimensions,
                    radius: Number(labelProps.width) / 2,
                    width: Number(labelProps.width),
                    height: Number(labelProps.height),
                  };

                  state.currentLabel = validateCurrentLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 5: {
              const defaultLabel = data.find((label: any) => label.name === "SLR4085");

              if (!defaultLabel) {
                throw new Error("No default label present, case 5");
              } else {
                const labelProps = getCurrentLabelProps(defaultLabel);

                if (validateCurrentLabelProps(labelProps)) {
                  state.dimensions = {
                    ...state.dimensions,
                    radius: Number(labelProps.width) / 2,
                    width: Number(labelProps.width),
                    height: Number(labelProps.height),
                  };

                  state.currentLabel = validateCurrentLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 6: {
              break;
            }
            case 7: {
              break;
            }
            case 8: {
              const defaultLabel = data.find((label: any) => label.name === "SLR5449");

              if (!defaultLabel) {
                throw new Error("No default label present, case 8");
              } else {
                const labelProps = getCurrentLabelProps(defaultLabel);

                if (validateCurrentLabelProps(labelProps)) {
                  state.dimensions = {
                    ...state.dimensions,
                    radius: Number(labelProps.width) / 2,
                    width: Number(labelProps.width),
                    height: Number(labelProps.height),
                  };

                  state.currentLabel = validateCurrentLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            case 9: {
              break;
            }
            case 10: {
              const defaultLabel = data.find((label: any) => label.name === "SLR4071");

              if (!defaultLabel) {
                throw new Error("No default label present, case 10");
              } else {
                const labelProps = getCurrentLabelProps(defaultLabel);

                if (validateCurrentLabelProps(labelProps)) {
                  state.dimensions = {
                    ...state.dimensions,
                    radius: Number(labelProps.width) / 2,
                    width: Number(labelProps.width),
                    height: Number(labelProps.height),
                  };

                  state.currentLabel = validateCurrentLabelProps(labelProps);
                } else {
                  throw new Error(`Error when validating label, MutationTypes.FETCH_SIZES_SUCCESS`);
                }
              }
              break;
            }
            default: {
              throw new Error("No Shape Id!");
            }
          }
          break;
        }
      }
    }
  },
  [MutationTypes.FETCH_SIZES_FAILURE](state: State, error: any): void {
    state.labelSizesFetch = {
      ...state.labelSizesFetch,
      isFetching: false,
      error: error,
    };
  },
};

const getSheetLabelDimensions = (labelProps: SheetLabel, dimensions: Dimensions) => {
  const bleed = labelProps.bleed ? (labelProps.bleed_size ? labelProps.bleed_size : 0.125) : 0.125;

  dimensions = {
    ...dimensions,
    units: {
      ...dimensions.units,
      offSet: bleed,
      offSetPx: bleed * dimensions.units.dpi,
    },
    radius: Number(labelProps.width) / 2,
    width: Number(labelProps.width),
    height: Number(labelProps.height),
  };

  return dimensions;
};
